import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

interface TimeDivider {
  unit: string;
  divider: number,
  unitTranslationRootKey: string
}

interface TimeDividerResult {
  timeDivider: TimeDivider;
  value: number;
  rest: number;
}

@Pipe({
  name: 'secondsDuration'
})
export class SecondsDurationPipe implements PipeTransform {

  private units: TimeDivider[] = [
    { unit: 'seconds', divider: 1, unitTranslationRootKey: 'time.seconds' },
    { unit: 'min', divider: 60, unitTranslationRootKey: 'time.minutes' },
    { unit: 'hours', divider: 60 * 60, unitTranslationRootKey: 'time.hours' },
    { unit: 'days', divider: 60 * 60 * 24, unitTranslationRootKey: 'time.days' },
    { unit: 'weeks', divider: 60 * 60 * 24 * 7, unitTranslationRootKey: 'time.weeks' },
    { unit: 'months', divider: 60 * 60 * 24 * 30, unitTranslationRootKey: 'time.months' },
    { unit: 'years', divider: 60 * 60 * 24 * 365, unitTranslationRootKey: 'time.years' }
  ];

  constructor(
    private translate: TranslateService
  ) {
  }

  transform(duration: number = 0, nbUnits = 1, hideSeconds: boolean = false): Observable<any> {
    let result: TimeDividerResult[] = [];

    for (let i = 0; i < nbUnits; i++) {
      const dividerResult = this.divide(duration);

      if (dividerResult) {
        result.push(dividerResult);
        duration = dividerResult.rest;
      } else {
        break;
      }
    }

    if (hideSeconds) {
      result = result.filter(divider => divider.timeDivider.unit !== 'seconds');
    }

    if (result.length === 0) {
      return of('');
    }

    return forkJoin(result.map(d => this.translate.get(this.getUnitTranslationKey(d), { duration: d.value })))
      .pipe(map(result => result.join(' ')));
  }

  private divide(duration: number): TimeDividerResult {
    let timeDivider: TimeDivider;
    let div, rest;
    let lastDiv;
    let lastRest;

    for (const td of this.units) {
      [div, rest] = this.divmod(duration, td.divider);

      if (div >= 1) {
        timeDivider = td;
      } else {
        return timeDivider ? { timeDivider: timeDivider, value: lastDiv, rest: lastRest } : null;
      }

      lastDiv  = div;
      lastRest = rest;
    }

    return timeDivider ? { timeDivider: timeDivider, value: lastDiv, rest: lastRest } : null;
  }

  private getUnitTranslationKey(d: TimeDividerResult): string {
    return d.value > 1 ? d.timeDivider.unitTranslationRootKey + '.plural' : d.timeDivider.unitTranslationRootKey + '.singular'
  }

  divmod(x, y) {
    const div = Math.trunc(x / y);
    const rem = x % y;
    return [div, rem];
  }

}
